import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

import { chartColors } from '../../helpers/chartColors';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';

export const RTFOND2StepdownChart = ({ gridView, sdtType }) => {

  const theme = useTheme();

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  
  
  const data = useMemo(() => {
    const stepdownData = sdtType === 'flush' ? currentStage?.stepdownData : sdtType === 'preSand' ? currentStage?.preSandStepdownData : [];
    const stepdownParams = currentStage?.stepdownParams || {};
    
    if (!stepdownData || !stepdownParams) return [];

    const ND2 = stepdownParams?.N * Math.pow(stepdownParams?.Dperf, 2);
    const kPerf = (0.2369 * stepdownParams?.rho / Math.pow(stepdownParams?.Cd * ND2, 2)) * 1970.031419;

    return getChartData();

    function getChartData() {
      try {

        const X = [];
        const YNumber = [];
        const YEfficiency = [];

        for (let d = 0.3; d <= 2; d += 0.005) {
          const N = Math.pow((16 * stepdownParams.rho / 0.0083454 / (kPerf * 1e6) / Math.pow(Math.PI, 2)), 0.5) / stepdownParams.Cd / Math.pow(d * 25.4 / 1000, 2);
          const perfEfficiency = N / stepdownParams.numShots * 100;
          YNumber.push(N);
          YEfficiency.push(perfEfficiency);
          X.push(d * 25.4);
        };

        // add traces
        const ND2Trace = {
          x: X,
          y: YNumber,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Num Shots',
          line: {
            width: 3,
            color: chartColors[2]
          },
          marker: {
            color: 'transparent',
            size: 0
          },
          hovertemplate: `D: %{x:.2f} mm \n N: %{y:.0f}`
        };
        const ND2EfficiencyTrace = {
          x: X,
          y: YEfficiency,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y2',
          name: 'Efficiency',
          line: {
            width: 3,
            color: chartColors[6]
          },
          marker: {
            color: 'transparent',
            size: 0
          },
          hovertemplate: `D: %{x:.2f} mm \n Eff.: %{y:.1f}%`
        };

        return [ 
          ND2Trace,
          ND2EfficiencyTrace
        ];
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentStage?.preSandStepdownData, currentStage?.stepdownData, currentStage?.stepdownParams, sdtType]);

  const layout = {
    yaxis: { // y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Number of Open Perfs',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [0, 25],
      // range: [0, currentStage?.stepdownParams?.numShots],
      side: 'left',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: 'toaxis'
    },
    yaxis2: { // y axis
      linecolor: chartColors[6],
      showgrid: false,
      // gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[6],
      title: {
        text: 'Perf Efficiency (%)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [0, 100],
      overlaying: 'y',
      side: 'right',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: 'toaxis'
    },
    xaxis: { // x axis
      range: [0.3 * 25.4, 2 * 25.4],
      title: {
        text: 'Post Erosion Perf Diameter (mm)',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "toaxis",
    },
    // hovermode: "x+y",
    // hoverdistance: 100, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: 'ND<sup>2</sup> Plot',
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.075 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: (gridView ? 0.05 : 0.025),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};

export const RTFOND2StepdownChartMemo = React.memo(RTFOND2StepdownChart);